import { ChangeEvent, Dispatch, ReactNode, SetStateAction, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ChevronDown, ChevronUp, Heading2, ImageIcon, List, Loader2, Plus, Quote, Table2, Trash2, Type, Video } from "lucide-react";

import { cn } from "@/libs/cn";
import Button from "@/modules/common/components/files/Button";
import { inputClassNameValues } from "@/modules/common/components/files/Input";
import { getApiErrorMessage } from "@/modules/common/utils/apiError";

import { BlockKind, ContentBlock, createBlock } from "../markdown";
import { isUploadingSource, uploadingFileName, uploadingSource } from "../media/media-placeholder";
import { MediaUploader, uploadKnowledgeBaseMedia } from "../media/media-upload";

const BLOCK_ACTIONS: { kind: BlockKind; label: string; icon: typeof Type }[] = [
  { kind: "paragraph", label: "Texto", icon: Type },
  { kind: "heading", label: "Subtítulo", icon: Heading2 },
  { kind: "list", label: "Lista", icon: List },
  { kind: "image", label: "Imagem", icon: ImageIcon },
  { kind: "video", label: "Vídeo", icon: Video },
  { kind: "table", label: "Tabela", icon: Table2 },
  { kind: "callout", label: "Destaque", icon: Quote },
];

const BLOCK_LABEL: Record<BlockKind, string> = {
  paragraph: "Parágrafo",
  heading: "Subtítulo",
  list: "Lista",
  image: "Imagem",
  video: "Vídeo",
  table: "Tabela",
  callout: "Destaque",
};

interface BlockEditorProps {
  blocks: ContentBlock[];
  disabled?: boolean;
  uploadFile?: MediaUploader;
  onChange: Dispatch<SetStateAction<ContentBlock[]>>;
}

const fieldClassName = cn(inputClassNameValues, "h-auto min-h-11");

const BlockEditor = ({ blocks, disabled = false, uploadFile = uploadKnowledgeBaseMedia, onChange }: BlockEditorProps) => {
  const update = (id: string, patch: Partial<ContentBlock>) => {
    onChange((current) => current.map((block) => (block.id === id ? { ...block, ...patch } : block)));
  };

  const move = (index: number, direction: -1 | 1) => {
    onChange((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      const [item] = next.splice(index, 1);
      next.splice(nextIndex, 0, item);
      return next;
    });
  };

  const remove = (id: string) => onChange((current) => current.filter((block) => block.id !== id));

  return (
    <div className="flex flex-col gap-4">
      {!disabled && (
        <div className="flex flex-wrap gap-2">
          {BLOCK_ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.kind}
                type="button"
                onClick={() => onChange((current) => [...current, createBlock(action.kind)])}
                className="inline-flex h-9 items-center gap-2 rounded-full border border-ui-border bg-white px-3 text-sm text-ui-primary transition hover:border-lime-500 hover:bg-[#F7FBEA]"
              >
                <Icon size={15} />
                {action.label}
              </button>
            );
          })}
        </div>
      )}

      {!blocks.length && (
        <div className="rounded-2xl border border-dashed border-ui-border bg-[#FAFBFC] px-6 py-14 text-center">
          <p className="text-sm font-medium text-ui-primary">O artigo ainda está em branco</p>
          <p className="mt-1 text-sm text-ui-text-muted">Adicione um bloco para montar o conteúdo.</p>
        </div>
      )}

      {blocks.map((block, index) => (
        <article key={block.id} className="rounded-2xl border border-ui-border bg-white p-4 shadow-sm">
          <header className="mb-3 flex items-center justify-between gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-ui-text-muted">{BLOCK_LABEL[block.kind]}</span>
            {!disabled && (
              <div className="flex items-center gap-1">
                <IconButton label="Mover para cima" onClick={() => move(index, -1)} disabled={index === 0}>
                  <ChevronUp size={16} />
                </IconButton>
                <IconButton label="Mover para baixo" onClick={() => move(index, 1)} disabled={index === blocks.length - 1}>
                  <ChevronDown size={16} />
                </IconButton>
                <IconButton label="Remover bloco" onClick={() => remove(block.id)}>
                  <Trash2 size={16} />
                </IconButton>
              </div>
            )}
          </header>

          {block.kind === "paragraph" || block.kind === "callout" ? (
            <textarea
              disabled={disabled}
              value={block.text}
              rows={block.kind === "callout" ? 3 : 4}
              placeholder={block.kind === "callout" ? "Uma observação que merece destaque" : "Escreva o parágrafo"}
              onChange={(event) => update(block.id, { text: event.target.value })}
              className={cn(fieldClassName, "resize-y py-3")}
            />
          ) : null}

          {block.kind === "heading" && (
            <input
              disabled={disabled}
              value={block.text}
              placeholder="Título da seção"
              onChange={(event) => update(block.id, { text: event.target.value })}
              className={fieldClassName}
            />
          )}

          {block.kind === "list" && (
            <div className="flex flex-col gap-2">
              {block.items.map((item, itemIndex) => (
                <div key={`${block.id}-${itemIndex}`} className="flex gap-2">
                  <input
                    disabled={disabled}
                    value={item}
                    placeholder={`Item ${itemIndex + 1}`}
                    onChange={(event) => {
                      const items = [...block.items];
                      items[itemIndex] = event.target.value;
                      update(block.id, { items });
                    }}
                    className={fieldClassName}
                  />
                  {!disabled && block.items.length > 1 && (
                    <IconButton
                      label="Remover item"
                      onClick={() => update(block.id, { items: block.items.filter((_, current) => current !== itemIndex) })}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  )}
                </div>
              ))}
              {!disabled && (
                <Button
                  type="button"
                  variant="text"
                  color="primary"
                  size="small"
                  icon={<Plus size={14} />}
                  label="Adicionar item"
                  onClick={() => update(block.id, { items: [...block.items, ""] })}
                />
              )}
            </div>
          )}

          {(block.kind === "image" || block.kind === "video") && (
            <MediaBlock block={block} disabled={disabled} uploadFile={uploadFile} onChange={(patch) => update(block.id, patch)} />
          )}

          {block.kind === "table" && <TableBlock block={block} disabled={disabled} onChange={(patch) => update(block.id, patch)} />}
        </article>
      ))}
    </div>
  );
};

const IconButton = ({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className="inline-flex size-8 items-center justify-center rounded-lg text-ui-text-muted hover:bg-gray-100 hover:text-ui-primary disabled:opacity-30"
  >
    {children}
  </button>
);

const MediaBlock = ({
  block,
  disabled,
  uploadFile,
  onChange,
}: {
  block: ContentBlock;
  disabled: boolean;
  uploadFile: MediaUploader;
  onChange: (patch: Partial<ContentBlock>) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const uploadingName = isUploadingSource(block.key) ? uploadingFileName(block.key) : undefined;

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const expected = block.kind === "video" ? "video/" : "image/";
    if (!file.type.startsWith(expected)) {
      toast.error(block.kind === "video" ? "Envie um arquivo de vídeo." : "Envie um arquivo de imagem.");
      return;
    }

    const previous = { key: block.key, previewUrl: block.previewUrl };
    setUploadPercent(0);
    onChange({ key: uploadingSource(file), previewUrl: "", alt: block.alt || file.name });
    try {
      const uploaded = await uploadFile(file, { onProgress: setUploadPercent });
      onChange({ key: uploaded.key, previewUrl: uploaded.url });
    } catch (error) {
      onChange(previous);
      toast.error(getApiErrorMessage(error, "Não foi possível enviar o arquivo."));
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <input
        disabled={disabled}
        value={block.alt}
        placeholder="Descrição do arquivo"
        onChange={(event) => onChange({ alt: event.target.value })}
        className={fieldClassName}
      />
      {uploadingName ? (
        <div className="flex flex-col gap-3 rounded-xl border border-dashed border-[#B5D66A] bg-[#F7FBEA] px-4 py-4">
          <div className="flex items-center gap-3">
            <Loader2 size={20} className="shrink-0 animate-spin text-[#99C32C]" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ui-primary">Enviando… {uploadPercent}%</span>
              <span className="block truncate text-xs text-ui-text-muted">{uploadingName}</span>
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-[#DCEBB4]">
            <div className="h-full rounded-full bg-[#99C32C] transition-[width] duration-300" style={{ width: `${uploadPercent}%` }} />
          </div>
        </div>
      ) : block.previewUrl ? (
        block.kind === "video" ? (
          <video src={block.previewUrl} controls className="max-h-72 w-full rounded-xl bg-black" />
        ) : (
          <img src={block.previewUrl} alt={block.alt} className="max-h-72 w-full rounded-xl object-contain" />
        )
      ) : (
        <p className="text-sm text-ui-text-muted">
          {block.key ? "Arquivo salvo. A prévia aparece depois de recarregar." : "Nenhum arquivo selecionado."}
        </p>
      )}
      {!disabled && (
        <>
          <input ref={inputRef} type="file" accept={block.kind === "video" ? "video/*" : "image/*"} className="hidden" onChange={upload} />
          <Button
            type="button"
            variant="outlined"
            color="primary"
            label={block.key ? "Trocar arquivo" : "Enviar arquivo"}
            disabled={Boolean(uploadingName)}
            onClick={() => inputRef.current?.click()}
          />
        </>
      )}
    </div>
  );
};

const TableBlock = ({
  block,
  disabled,
  onChange,
}: {
  block: ContentBlock;
  disabled: boolean;
  onChange: (patch: Partial<ContentBlock>) => void;
}) => {
  const resize = (headers: string[]) => {
    onChange({
      headers,
      rows: block.rows.map((row) => headers.map((_, index) => row[index] ?? "")),
    });
  };

  return (
    <div className="flex flex-col gap-3 overflow-x-auto">
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${block.headers.length}, minmax(140px, 1fr))` }}>
        {block.headers.map((header, index) => (
          <input
            key={`header-${index}`}
            disabled={disabled}
            value={header}
            onChange={(event) => {
              const headers = [...block.headers];
              headers[index] = event.target.value;
              onChange({ headers });
            }}
            className={cn(fieldClassName, "bg-gray-50 font-medium")}
          />
        ))}
        {block.rows.map((row, rowIndex) =>
          row.map((cell, columnIndex) => (
            <input
              key={`${rowIndex}-${columnIndex}`}
              disabled={disabled}
              value={cell}
              onChange={(event) => {
                const rows = block.rows.map((current, currentIndex) =>
                  currentIndex === rowIndex ? current.map((value, index) => (index === columnIndex ? event.target.value : value)) : current,
                );
                onChange({ rows });
              }}
              className={fieldClassName}
            />
          )),
        )}
      </div>
      {!disabled && (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="text"
            color="primary"
            size="small"
            icon={<Plus size={14} />}
            label="Linha"
            onClick={() => onChange({ rows: [...block.rows, block.headers.map(() => "")] })}
          />
          <Button
            type="button"
            variant="text"
            color="primary"
            size="small"
            icon={<Plus size={14} />}
            label="Coluna"
            onClick={() => resize([...block.headers, `Coluna ${block.headers.length + 1}`])}
          />
        </div>
      )}
    </div>
  );
};

export default BlockEditor;
